import { colors } from '@globals/themes/defaultTheme'
import Box from '@mui/material/Box'
import { SxProps } from '@mui/material/styles'
import BottomDrawer from '@shared/dialog/BottomDrawer'
import FormContextWrapper from '@shared/forms/FormContextWrapper'
import useQualities from '@shared/hooks/useQualities'
import useSendRecognitionForm, {
  RecognitionFormData,
} from '@tenant/recognition/RecognitionModal/sendRecognitionView/useSendRecognitionForm'
import { useSendRecognitionFormStore } from '@tenant/recognition/RecognitionModal/sendRecognitionView/useSendRecognitionFormStore'
import useDraftRecognitionsCount from '@tenant/recognition/RecognitionModal/useDraftRecognitionCount'
import { T } from '@transifex/react'
import { isEmpty } from 'lodash'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import MobileRecognitionDraftView from './DraftAndScheduleView/MobileRecognitionDraftView'
import MobileRecognitionPointsExceptionView from './MobileRecognitionPointsExceptionView'
import MobileRecognitionPointsView from './MobileRecognitionPointsView'
import MobileRecognitionQualitiesView from './MobileRecognitionQualitiesView'
import MobileRecognitionRecipientsView from './MobileRecognitionRecipientsView'
import MobileRecognitionScheduleView from './MobileRecognitionScheduleView'
import MobileRecognitionVisibilityView from './MobileRecognitionVisibilityView'
import MobileRecognitionSendView from './SendView/MobileRecognitionSendView'
import useMobileRecognitionDrawer, {
  MOBILE_RECOGNITION_DRAWER_KEY,
  MobileRecognitionDrawerView,
} from './useMobileRecognitionDrawer'
import useMobileRecognitionViewActions from './useMobileRecognitionViewActions'

const mobileActionStyle: SxProps = {
  position: 'fixed',
  bottom: 16,
  left: 16,
  right: 16,
  display: 'flex',
  justifyContent: 'flex-start',
  zIndex: 900, // keep under the onboarding FAB (zIndex 1000)
}

const sendDrawerHeaderStyles: SxProps = {
  '& .MuiDrawer-paper': {
    borderRadius: 0,
    border: 'none',
  },
  '& #drawer-header': {
    background: `${colors.blueCardGradient} !important`,
    '& .MuiTypography-root': {
      color: `${colors.white} !important`,
    },
  },
  // Only the header close button (direct child of paper) — not the Send/Draft action buttons
  '& .MuiDrawer-paper > .MuiButton-root': {
    color: `${colors.white} !important`,
  },
}

const noopShowAnimation = (_show: boolean) => {
  /* no-op for mobile: animation is not used */
}

const DEFAULT_TITLE = <T _str="Recognize someone" />

const viewTitles: Record<MobileRecognitionDrawerView, ReactNode> = {
  [MobileRecognitionDrawerView.DRAFT_AND_SCHEDULED]: DEFAULT_TITLE,
  [MobileRecognitionDrawerView.RECIPIENTS]: DEFAULT_TITLE,
  [MobileRecognitionDrawerView.SEND]: DEFAULT_TITLE,
  [MobileRecognitionDrawerView.VISIBILITY]: <T _str="Set post visibility" />,
  [MobileRecognitionDrawerView.QUALITIES]: <T _str="Add qualities" />,
  [MobileRecognitionDrawerView.POINTS]: <T _str="Add points" />,
  [MobileRecognitionDrawerView.SCHEDULE]: <T _str="Schedule post" />,
  [MobileRecognitionDrawerView.POINTS_EXCEPTION]: DEFAULT_TITLE,
}

const viewContent: Record<MobileRecognitionDrawerView, ReactNode> = {
  [MobileRecognitionDrawerView.DRAFT_AND_SCHEDULED]: <MobileRecognitionDraftView />,
  [MobileRecognitionDrawerView.RECIPIENTS]: <MobileRecognitionRecipientsView />,
  [MobileRecognitionDrawerView.SEND]: <MobileRecognitionSendView />,
  [MobileRecognitionDrawerView.VISIBILITY]: <MobileRecognitionVisibilityView />,
  [MobileRecognitionDrawerView.QUALITIES]: <MobileRecognitionQualitiesView />,
  [MobileRecognitionDrawerView.POINTS]: <MobileRecognitionPointsView />,
  [MobileRecognitionDrawerView.SCHEDULE]: <MobileRecognitionScheduleView />,
  [MobileRecognitionDrawerView.POINTS_EXCEPTION]: <MobileRecognitionPointsExceptionView />,
}

const MobileRecognitionDrawer = () => {
  const { closeDrawer, dialogData, isOpen } = useMobileRecognitionDrawer()
  const { draftRecognitionCount } = useDraftRecognitionsCount({ enabled: isOpen })
  const { qualities } = useQualities()
  const [showDraftButton, setShowDraftButton] = useState(false)
  const { setIsFormDirty } = useSendRecognitionFormStore()
  const currentView = dialogData?.view ?? MobileRecognitionDrawerView.DRAFT_AND_SCHEDULED
  const recognition = dialogData?.recognition

  const {
    form,
    isFormDirty,
    pointsExceptions,
    // setPointsExceptions,
    publishRecognition,
    handleSaveAsDraft,
    handleResetRecognitionForm,
    isPendingCreateOrSave,
  } = useSendRecognitionForm(noopShowAnimation, !isEmpty(qualities), recognition, closeDrawer)

  const viewActions = useMobileRecognitionViewActions(
    form,
    handleSaveAsDraft,
    isPendingCreateOrSave,
    showDraftButton,
  )

  const submitForm = async (data: RecognitionFormData) => {
    return publishRecognition(data, !!pointsExceptions, handleResetRecognitionForm)
  }

  useEffect(() => {
    setIsFormDirty(isFormDirty)
  }, [isFormDirty, setIsFormDirty])

  useEffect(() => {
    setShowDraftButton(currentView === MobileRecognitionDrawerView.SEND)
  }, [currentView])

  const effectiveView = useMemo(() => {
    if (
      currentView === MobileRecognitionDrawerView.DRAFT_AND_SCHEDULED &&
      !(draftRecognitionCount?.total && draftRecognitionCount.total > 0)
    ) {
      return MobileRecognitionDrawerView.RECIPIENTS
    }
    return currentView
  }, [currentView, draftRecognitionCount?.total])

  const actionContent = viewActions[effectiveView]

  return (
    <BottomDrawer
      drawerKey={MOBILE_RECOGNITION_DRAWER_KEY}
      title={viewTitles[effectiveView]}
      backgroundColor={colors.grayUltraLight}
      onClose={() => {
        handleResetRecognitionForm()
        closeDrawer()
      }}
      drawerStyles={
        MobileRecognitionDrawerView.SEND === effectiveView ? sendDrawerHeaderStyles : undefined
      }
      actionButtonCount={showDraftButton ? 2 : 1}>
      <FormContextWrapper formMethods={form} onSubmit={submitForm}>
        <Box sx={{ py: 2 }}>{viewContent[effectiveView]}</Box>
        {actionContent && <Box sx={mobileActionStyle}>{actionContent}</Box>}
      </FormContextWrapper>
    </BottomDrawer>
  )
}

export default MobileRecognitionDrawer
